/** Fresh castle enclosures: long cardinal runs fitted wholly onto one dry bank. */
import { nextInt, type Rng } from '../../core/rng.ts';
import { Terrain, WallKind } from '../types.ts';
import type { World } from '../world.ts';

export interface TownPoint { x: number; y: number }
export interface TownBounds { x0: number; y0: number; x1: number; y1: number }
export interface TownGate extends TownPoint { dx: number; dy: number; tiles: TownPoint[] }
export interface TownCircuit {
  centre: TownPoint; bounds: TownBounds; tiles: TownPoint[]; corners: TownPoint[];
  gates: TownGate[]; interior: Uint8Array;
}

function dry(w: World, x: number, y: number): boolean {
  if (!w.map.inBounds(x, y)) return false;
  const t = w.map.terrain[w.map.idx(x, y)];
  // Marsh is traversable ground; scattered bog tiles must not prevent a whole
  // landward curtain. Buildings still reject marsh through plotFree.
  return t !== Terrain.River && t !== Terrain.Deep && t !== Terrain.Bridge;
}

/** Largest dry rectangle containing the protected core; water is never filled. */
function fitBank(w: World, limit: TownBounds, core: TownBounds): TownBounds {
  const width = limit.x1 - limit.x0 + 1, height = limit.y1 - limit.y0 + 1, stride = width + 1;
  const sum = new Int32Array((width + 1) * (height + 1));
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    const at = (y + 1) * stride + x + 1;
    sum[at] = (dry(w, x + limit.x0, y + limit.y0) ? 0 : 1) + sum[at - 1] + sum[at - stride] - sum[at - stride - 1];
  }
  const wet = (x0: number, y0: number, x1: number, y1: number): number => {
    const a = x0 - limit.x0, b = x1 - limit.x0 + 1, c = y0 - limit.y0, d = y1 - limit.y0 + 1;
    return sum[d * stride + b] - sum[c * stride + b] - sum[d * stride + a] + sum[c * stride + a];
  };
  let best: TownBounds | null = null, score = -Infinity;
  for (let x0 = limit.x0; x0 < core.x0; x0++) for (let x1 = core.x1 + 1; x1 <= limit.x1; x1++) {
    if (wet(x0, core.y0, x1, core.y1)) continue;
    let y0 = core.y0, y1 = core.y1;
    while (y0 > limit.y0 && !wet(x0, y0 - 1, x1, y0 - 1)) y0--;
    while (y1 < limit.y1 && !wet(x0, y1 + 1, x1, y1 + 1)) y1++;
    if (y0 >= core.y0 || y1 <= core.y1) continue;
    const area = (x1 - x0) * (y1 - y0);
    const centred = Math.abs(x0 + x1 - core.x0 - core.x1) + Math.abs(y0 + y1 - core.y0 - core.y1);
    const value = area * 10 - centred;
    if (value > score) { best = { x0, y0, x1, y1 }; score = value; }
  }
  if (!best) throw new Error('No dry curtain can enclose the protected town core');
  return best;
}

/** Flood only the enclosed ground. Gates count as curtain until opened in play. */
export function townInterior(w: World, bounds: TownBounds, centre: TownPoint, curtain?: ReadonlySet<number>): Uint8Array {
  const inside = new Uint8Array(w.map.terrain.length), queue: number[] = [];
  const blocked = (k: number) => curtain ? curtain.has(k) : w.map.wall[k] !== WallKind.None;
  const first = w.map.idx(centre.x, centre.y);
  if (blocked(first)) return inside;
  queue.push(first); inside[first] = 1;
  for (let n = 0; n < queue.length; n++) {
    const at = queue[n], x = at % w.map.w, y = Math.floor(at / w.map.w);
    for (const [nx, ny] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]]) {
      if (nx <= bounds.x0 || ny <= bounds.y0 || nx >= bounds.x1 || ny >= bounds.y1) continue;
      const k = w.map.idx(nx, ny);
      if (!inside[k] && !blocked(k)) { inside[k] = 1; queue.push(k); }
    }
  }
  return inside;
}

export function planTownCircuit(w: World, rng: Rng, cx: number, cy: number, radius: number, core: TownBounds): TownCircuit {
  const limit = {
    x0: Math.max(3, Math.min(core.x0 - 2, cx - radius + nextInt(rng, 0, 2))),
    y0: Math.max(3, Math.min(core.y0 - 2, cy - radius + nextInt(rng, 0, 2))),
    x1: Math.min(w.map.w - 4, Math.max(core.x1 + 2, cx + radius - nextInt(rng, 0, 2))),
    y1: Math.min(w.map.h - 4, Math.max(core.y1 + 2, cy + radius - nextInt(rng, 0, 2))),
  };
  let bounds = fitBank(w, limit, core);
  // A river can squeeze the nominal square into a narrow strip. Extend along
  // the same bank until there is room for the city's programme, keeping its
  // existing core in place and leaving the channel outside the enclosure.
  for (let extra = 2; extra <= radius * 2
    && (bounds.x1 - bounds.x0) * (bounds.y1 - bounds.y0) < radius * radius * 2; extra += 2) {
    bounds = fitBank(w, {
      x0: Math.max(3, limit.x0 - extra), y0: Math.max(3, limit.y0 - extra),
      x1: Math.min(w.map.w - 4, limit.x1 + extra), y1: Math.min(w.map.h - 4, limit.y1 + extra),
    }, core);
  }
  const { x0, y0, x1, y1 } = bounds;
  // Unequal corner recesses make a deliberate bailey, not radial tile noise.
  const cut = (space: number) => Math.max(0, Math.min(space - 2, nextInt(rng, 2, Math.max(2, Math.round(radius * .3)))));
  const nw = { x: cut(core.x0 - x0), y: cut(core.y0 - y0) };
  const ne = { x: cut(x1 - core.x1), y: cut(core.y0 - y0) };
  const se = { x: cut(x1 - core.x1), y: cut(y1 - core.y1) };
  const sw = { x: cut(core.x0 - x0), y: cut(y1 - core.y1) };
  const raw: TownPoint[] = [
    {x:x0+nw.x,y:y0},{x:x1-ne.x,y:y0},{x:x1-ne.x,y:y0+ne.y},{x:x1,y:y0+ne.y},
    {x:x1,y:y1-se.y},{x:x1-se.x,y:y1-se.y},{x:x1-se.x,y:y1},{x:x0+sw.x,y:y1},
    {x:x0+sw.x,y:y1-sw.y},{x:x0,y:y1-sw.y},{x:x0,y:y0+nw.y},{x:x0+nw.x,y:y0+nw.y},
  ];
  const corners = raw.filter((p, i) => p.x !== raw[(i + raw.length - 1) % raw.length].x || p.y !== raw[(i + raw.length - 1) % raw.length].y);
  const seen = new Set<number>(), tiles: TownPoint[] = [];
  for (let n = 0; n < corners.length; n++) {
    const a = corners[n], b = corners[(n + 1) % corners.length], dx = Math.sign(b.x - a.x), dy = Math.sign(b.y - a.y);
    const length = Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
    for (let step = 0; step < length; step++) {
      const p = { x: a.x + dx * step, y: a.y + dy * step }, k = w.map.idx(p.x, p.y);
      if (!seen.has(k)) { seen.add(k); tiles.push(p); }
    }
  }
  const centre = { x: Math.round((core.x0 + core.x1) / 2), y: Math.round((core.y0 + core.y1) / 2) };
  const interior = townInterior(w, bounds, centre, seen), candidates: Array<TownGate & { score: number }> = [];
  for (let n = 0; n < corners.length; n++) {
    const a = corners[n], b = corners[(n + 1) % corners.length], alongX = Math.sign(b.x - a.x), alongY = Math.sign(b.y - a.y);
    const dx = alongY, dy = -alongX, length = Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
    for (let step = 3; step <= length - 3; step++) {
      const x = a.x + alongX * step, y = a.y + alongY * step;
      const gateTiles = [-1,0,1].map(offset => ({x:x+alongX*offset,y:y+alongY*offset}));
      let clear = true, street = false;
      for (const p of gateTiles) for (let depth = -2; depth <= 3; depth++) {
        const tx = p.x + dx * depth, ty = p.y + dy * depth;
        if (!dry(w, tx, ty) || w.map.building[w.map.idx(tx, ty)] >= 0) { clear = false; break; }
        const k = w.map.idx(tx, ty);
        if (depth < 0 && !interior[k]) clear = false;
        if (depth > 0 && (seen.has(k) || interior[k])) clear = false;
        if (w.map.terrain[k] === Terrain.Road) street = true;
      }
      if (clear) candidates.push({ x, y, dx, dy, tiles: gateTiles, score: Math.hypot(x-centre.x,y-centre.y) - (street ? radius : 0) });
    }
  }
  candidates.sort((a,b) => a.score-b.score || a.y-b.y || a.x-b.x);
  const gates: TownGate[] = [];
  const apart = (a: TownPoint) => gates.every(b => Math.abs(a.x-b.x)+Math.abs(a.y-b.y) >= 7);
  for (const [dx,dy] of [[0,-1],[1,0],[0,1],[-1,0]]) {
    const gate = candidates.find(g => g.dx===dx && g.dy===dy && apart(g));
    if (gate) gates.push(gate);
  }
  for (const gate of candidates) {
    if (gates.length >= 4) break;
    if (apart(gate)) gates.push(gate);
  }
  return { centre, bounds, tiles, corners, gates, interior };
}

/** Two tiles square, attached to a real bend and kept clear of gate throats. */
export function townTowerPlots(w: World, circuit: TownCircuit, count: number): TownPoint[] {
  const out: TownPoint[] = [], gateTiles = new Set(circuit.gates.flatMap(g => g.tiles.map(p => w.map.idx(p.x,p.y))));
  const remaining = [...circuit.corners];
  while (remaining.length && out.length < count) {
    // Spread towers round the bailey, without a stride that can skip corners.
    remaining.sort((a,b) => {
      const distance = (p: TownPoint) => out.length ? Math.min(...out.map(t => Math.abs(t.x-p.x)+Math.abs(t.y-p.y))) : 0;
      return distance(b)-distance(a) || a.y-b.y || a.x-b.x;
    });
    const p = remaining.shift()!;
    let best: TownPoint | null = null, bestScore = -1;
    for (const oy of [-1,0]) for (const ox of [-1,0]) {
      const x = p.x+ox, y = p.y+oy;
      if (out.some(t => Math.abs(t.x-x)<3 && Math.abs(t.y-y)<3)) continue;
      let clear = true, wall = 0, inside = 0;
      for (let dy=0;dy<2;dy++) for (let dx=0;dx<2;dx++) {
        const tx=x+dx,ty=y+dy,k=w.map.idx(tx,ty);
        if (!dry(w,tx,ty) || w.map.building[k]>=0 || gateTiles.has(k)) clear=false;
        if (w.map.wall[k]===WallKind.Wall) wall++;
        if (circuit.interior[k]) inside++;
      }
      const score=wall*5+inside;
      if (clear && wall>=2 && score>bestScore) { best={x,y};bestScore=score; }
    }
    if (best) out.push(best);
  }
  return out;
}

/** Short cardinal streets stay inside the bailey and go around the real Hearth. */
export function townStreet(w: World, inside: Uint8Array, from: TownPoint, to: TownPoint): number[] {
  const first=w.map.idx(from.x,from.y),last=w.map.idx(to.x,to.y),prev=new Int32Array(inside.length).fill(-1),queue=[first];
  prev[first]=first;
  for(let n=0;n<queue.length;n++) {
    const at=queue[n];if(at===last)break;
    const x=at%w.map.w,y=Math.floor(at/w.map.w);
    const dirs=Math.abs(to.x-x)>Math.abs(to.y-y)?[[1,0],[-1,0],[0,1],[0,-1]]:[[0,1],[0,-1],[1,0],[-1,0]];
    for(const[dx,dy]of dirs) {
      const tx=x+dx,ty=y+dy;if(!w.map.inBounds(tx,ty))continue;const k=w.map.idx(tx,ty);
      if(prev[k]>=0||!inside[k]||w.map.building[k]>=0||!dry(w,tx,ty))continue;
      prev[k]=at;queue.push(k);
    }
  }
  if(prev[last]<0)return[];
  const path=[last];while(path[path.length-1]!==first)path.push(prev[path[path.length-1]]);
  return path.reverse();
}
